<template>
  <div class="loader with-padding" v-if="!fetched">
    <v-progress-circular indeterminate color="primary"></v-progress-circular>
  </div>
  <div v-else-if="recording" class="recording">
    <input
      class="share-link-contents"
      ref="shareRecordingLink"
      type="text"
      :value="currentUrl"
    />
    <input
      class="share-link-contents"
      ref="shareRecordingLinkAtCurrentTime"
      type="text"
      :value="currentUrl + '?t=' + currentTime"
    />
    <div class="video-container">
      <div
        class="share"
      >
      <v-menu bottom left>
          <v-btn slot="activator" icon>
            <v-icon medium>link</v-icon>
          </v-btn>
          <v-list>
            <v-list-tile @click="shareLinkToRecording('shareRecordingLink')">
              <v-list-tile-title>Copy link to recording</v-list-tile-title>
            </v-list-tile>
            <v-list-tile @click="shareLinkToRecording('shareRecordingLinkAtCurrentTime')">
              <v-list-tile-title>Copy link at current time</v-list-tile-title>
            </v-list-tile>
          </v-list>
        </v-menu>
      </div>
      <!-- Recording video component -->
      <RecordingVideo
        :video-url="recording.videos[0].url"
        :timestamp="this.$route && this.$route.query ? this.$route.query.t : 0"
      />
      <!-- Video Controls -->
      <div id="video-controls">
        <div class="playback-area">
          <button @click="playPause" type="button" id="play-pause">
            <v-icon v-if="!videoPlaying" class="play-arrow">play_arrow</v-icon>
            <v-icon v-if="videoPlaying" class="pause">pause</v-icon>
          </button>
          <span class="video-times">
            {{ formattedCurrentVideoTime }} / {{ formattedVideoDuration }}
          </span>
        </div>
        <div
          id="seek-bar"
          @click="updateVideoBySeeking"
          ref="the_seek_bar"
        >
          <span class="current-video-pos" :style="{ left: seekBarPercent + '%' }" />
        </div>
      </div>
      <div v-if="videoError" class="error">
        <HazardIcon />
        <h2>
          An error occurred while trying to load the video.
        </h2>
        <p>
          Perhaps it's not finished uploading yet? Try waiting a moment and reloading the page.
        </p>
      </div>

      <div v-if="seeking" class="seeking">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
      </div>
    </div>
    <!-- Issues Timeline -->
    <div class="timeline-wrapper">
      <div class="title">
        <p>Issues</p>
      </div>
      <span class="timeline">
        <img
          class="icon"
          src="../assets/issue-icon.png"
          alt="icon"
          v-for="item in issues"
          :key="item._id"
          :style="{ left: item.playbackPosPct + '%' }"
          @click="selectIssue(item)"
        />
      </span>
    </div>
    <!-- Events Timeline -->
    <div class="timeline-wrapper">
      <div class="title">
        <p>Events</p>
      </div>
      <span class="timeline">
        <img
          class="icon"
          src="../assets/issue-icon.png"
          alt="icon"
          v-for="item in events"
          :key="item._id"
          :style="{ left: item.playbackPosPct + '%' }"
          @click="seekToIcon(item.playbackPosInMS)"
        />
      </span>
    </div>
    <!-- Console -->
    <RecordingConsole />
  </div>
  <div class="error" v-else>
    <p>Something went wrong.</p>
  </div>
</template>

<script>
import _ from 'lodash';
import { mapState, mapGetters, mapActions } from 'vuex';
import RecordingVideo from './RecordingVideo';
import RecordingConsole from './RecordingConsole';
import HazardIcon from '../assets/noun_509635_EC5D57.vue';

export default {
  name: 'Recording',
  hagridActions: ['issues/getIssues'],
  components: {
    RecordingVideo,
    RecordingConsole,
    HazardIcon,
  },
  computed: {
    ...mapGetters({
      formattedCurrentVideoTime: 'recordings/formattedCurrentVideoTime',
      formattedVideoDuration: 'recordings/formattedVideoDuration',
      seekBarPercent: 'recordings/seekBarPercent',
      fetched: 'recordings/fetched',
      issues: 'recordings/issues',
      events: 'recordings/events',
      recording: 'recordings/current',
    }),
    ...mapState({
      videoPlaying: state => state.recordings.videoPlaying,
      duration: state => state.recordings.recordingDuration,
      seeking: state => state.recordings.seekingVideo,
      videoError: state => state.recordings.videoError,
      currentTime: state => Math.ceil(state.recordings.playbackPosInMS),
    }),
    currentUrl() { return `${window.location.protocol}//${location.host}${location.pathname}`; },
  },
  async mounted() {
    this.$store.dispatch(
      'recordings/fetchOne',
      {
        recordingId: this.$route.params.recordingId,
      },
    );
    if (this.$route.query && this.$route.query.issue_id) {
      await this.hagridPromise('issues/getIssues');
      const issue = _.find(this.issues, { _id: this.$route.query.issue_id });
      this.selectIssue(issue);
    }
  },
  methods: {
    // These actions all reference eventbus emit events
    ...mapActions({
      pauseVideo: 'recordings/pauseVideo',
      playVideo: 'recordings/playVideo',
      seekVideo: 'recordings/seekVideo',
      selectIssue: 'recordings/selectIssue',
    }),
    playPause() {
      if (this.videoPlaying) {
        this.pauseVideo();
      } else {
        this.playVideo();
      }
    },
    updateVideoBySeeking(e) {
      const seekBarWidth = this.$refs.the_seek_bar.offsetWidth;
      const mousePos = e.offsetX;
      const offsetSeekBarValue = mousePos / seekBarWidth;
      const seekBarValue = this.duration * offsetSeekBarValue;
      this.seekVideo(seekBarValue);
    },
    seekToIcon(value) {
      this.seekVideo(value);
    },
    shareLinkToRecording(ref) {
      this.$refs[ref].select();
      document.execCommand('copy');
      this.flashSuccess();
    },
    flashSuccess() {
      this.$store.dispatch(
        'Notifications/flashMessage',
        { msg: 'Link copied to clipboard!', msgType: 'success' });
    },
  },
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss" scoped>
p {
  margin: 0px;
}
.recording {
  display: flex;
  flex-direction: column;
}
.video-container {
  margin-top: 20px;
  position: relative;
  width: 1024px;
  align-self: center;
  border-bottom: 1px solid black;
}
#video-controls {
  display: flex;
	position: absolute;
	bottom: 0;
	left: 0;
	right: 0;
	opacity: 0;
	-webkit-transition: opacity .3s;
	-moz-transition: opacity .3s;
	-o-transition: opacity .3s;
	-ms-transition: opacity .3s;
	transition: opacity .3s;
	background-color: #313131;
}
.video-container:hover #video-controls {
	opacity: .9;
}
.share {
  z-index: 10;
  position: absolute;
  right: 0px;
  top: 20px;
}
.playback-area {
  background-color: #313131;
  width: 160px;
  padding: 5px;

}
#play-pause {
  background-color: #313131;
  border: none;
}
.play-arrow {
  color: white !important;
  background-color: #313131;
}
.pause {
  color: white !important;
  background-color: #313131;
}
.video-times {
  color: white;
  font-size: 12px;
}
#seek-bar {
  width: 100%;
  background-color: #ddeaea;
  position: relative;
}
.seeking {
  position: absolute;
  top: 50%;
  right: 50%;
}
.error {
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  svg {
    margin-top: 100px;
    margin-left: calc(50% - 50px);
    width: 100px;
  }
  h2, p {
    width: 100%;
    text-align: center;
  }
  p {
    font-size: 130%;
  }
}
.timeline-wrapper {
  display: flex;
  height: 40px;
  align-self: center;
  width: 1024px;
  .title {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 140px;
    height: 100%;
    background-color: #313131;
    color: white;
  }
  .timeline {
    display: flex;
    align-items: center;
    position: relative;
    background-color: #ddeaea;
    height: 100%;
    width: 884px;
    .icon {
      position: absolute;
      margin-left: -12px;
    }
  }
}
.console-wrapper {
  align-self: center;
  width: 1024px;
}
.current-video-pos {
  width: 4px;
	height: 100%;
	background-color: #e74c3c;
  position: absolute;
}
.loader {
  margin-top: 50px;
  display: flex;
  flex-grow: 1;
  align-items: center;
  justify-content: center;
}
.share-link-contents {
  position: absolute;
  top: -30px;
}
</style>



// WEBPACK FOOTER //
// src/components/Recording.vue